import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Chip,
  IconButton,
  FormControlLabel,
  Switch,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  VideoCall as VideoCallIcon,
  Schedule as ScheduleIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Launch as LaunchIcon,
  Email as EmailIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CallEnd as CallEndIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  RecordVoiceOver as TranscribeIcon
} from '@mui/icons-material';
import api from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import MeetingAnalyticsCard from '../Analytics/MeetingAnalyticsCard';

function HostDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [selectedMeetingForInvite, setSelectedMeetingForInvite] = useState(null);
  const [invitees, setInvitees] = useState([{ email: '', name: '' }]);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedMeetingForUpload, setSelectedMeetingForUpload] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [transcribing, setTranscribing] = useState({});
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    description: '',
    meetingType: 'scheduled',
    accessType: 'private',
    password: '',
    allowGuests: false,
    recordingEnabled: false,
    transcriptionEnabled: true,
    scheduledStartTime: '',
    scheduledEndTime: ''
  });

  useEffect(() => {
    document.title = 'Meeting Host Dashboard - EchoNote';
    fetchMeetings();
    
    // Poll for updates every 10 seconds to refresh conversion status
    // Only continue refreshing if user is still logged in
    const interval = setInterval(() => {
      if (user) {
        fetchMeetings();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [user]);

  const fetchMeetings = async () => {
    try {
      const response = await api.get('/hosted-meetings');
      setMeetings(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load meetings');
      setLoading(false);
    }
  };

  const handleCreateMeeting = async () => {
    try {
      // Prepare meeting data
      const meetingData = {
        ...newMeeting,
        meetingType: 'scheduled'
      };
      
      // Convert local datetime strings to ISO format if provided
      if (meetingData.scheduledStartTime) {
        meetingData.scheduledStartTime = new Date(meetingData.scheduledStartTime).toISOString();
      }
      if (meetingData.scheduledEndTime) {
        meetingData.scheduledEndTime = new Date(meetingData.scheduledEndTime).toISOString();
      }
      
      await api.post(
        '/hosted-meetings',
        meetingData
      );
      
      setOpenCreateDialog(false);
      fetchMeetings();
    } catch (err) {
      setError('Failed to create meeting');
    }
  };

  const handleStartInstantMeeting = async () => {
    try {
      const response = await api.post(
        '/hosted-meetings',
        {
          title: `Quick Meeting - ${new Date().toLocaleString()}`,
          meetingType: 'instant',
          accessType: 'private',
          allowGuests: false,
          recordingEnabled: false,
          transcriptionEnabled: true
        }
      );
      
      navigate(`/meeting-room/${response.data.id}`);
    } catch (err) {
      setError('Failed to start meeting');
    }
  };

  const handleDeleteMeeting = async (id) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;
    
    try {
      await api.delete(`/hosted-meetings/${id}`);
      fetchMeetings();
    } catch (err) {
      setError('Failed to delete meeting');
    }
  };

  const handleEndMeeting = async (id) => {
    if (!window.confirm('Are you sure you want to end this meeting?')) return;
    
    try {
      await api.post(`/hosted-meetings/${id}/end`, {});
      fetchMeetings();
    } catch (err) {
      setError('Failed to end meeting');
    }
  };

  const copyMeetingLink = (meetingCode) => {
    const link = `${window.location.origin}/join-meeting/${meetingCode}`;
    navigator.clipboard.writeText(link);
    alert('Meeting link copied to clipboard!');
  };

  const handleViewRecording = async (meeting) => {
    try {
      // Get the first completed or processing recording (processing recordings can still be viewed)
      const recording = meeting.recordings?.find(r => r.status === 'completed' || r.status === 'processing');
      
      if (!recording) {
        setError('No recording available for this meeting');
        return;
      }

      const response = await api.get(
        `/hosted-meetings/${meeting.id}/recordings/${recording.id}/download`
      );

      if (response.data.downloadUrl) {
        // Open the download URL in a new tab
        window.open(response.data.downloadUrl, '_blank');
      } else {
        setError('Recording download URL not available');
      }
    } catch (err) {
      console.error('Error getting recording download URL:', err);
      setError('Failed to get recording download URL');
    }
  };

  const handleOpenInviteDialog = (meeting) => {
    setSelectedMeetingForInvite(meeting);
    setInvitees([{ email: '', name: '' }]);
    setInviteSuccess('');
    setOpenInviteDialog(true);
  };

  const isEndedMeeting = (meeting) => {
    return meeting.status === 'ended';
  };

  const handleCloseInviteDialog = () => {
    setOpenInviteDialog(false);
    setSelectedMeetingForInvite(null);
    setInvitees([{ email: '', name: '' }]);
    setInviteSuccess('');
  };

  const handleAddInvitee = () => {
    setInvitees([...invitees, { email: '', name: '' }]);
  };

  const handleRemoveInvitee = (index) => {
    const newInvitees = invitees.filter((_, i) => i !== index);
    setInvitees(newInvitees.length > 0 ? newInvitees : [{ email: '', name: '' }]);
  };

  const handleInviteeChange = (index, field, value) => {
    const newInvitees = [...invitees];
    newInvitees[index][field] = value;
    setInvitees(newInvitees);
  };

  const handleSendInvitations = async () => {
    if (!selectedMeetingForInvite) return;

    // Filter out empty invitees
    const validInvitees = invitees.filter(inv => inv.email.trim() !== '');
    
    if (validInvitees.length === 0) {
      setError('Please add at least one recipient with an email address');
      return;
    }

    try {
      // Check if meeting has ended - send materials instead of invitations
      if (isEndedMeeting(selectedMeetingForInvite)) {
        const response = await api.post(
          `/hosted-meetings/${selectedMeetingForInvite.id}/send-materials`,
          { recipients: validInvitees }
        );

        setInviteSuccess(`Meeting materials sent successfully! ${response.data.successCount} sent, ${response.data.failureCount} failed.`);
      } else {
        const response = await api.post(
          `/hosted-meetings/${selectedMeetingForInvite.id}/invite`,
          { invitees: validInvitees }
        );

        setInviteSuccess(`Invitations sent successfully! ${response.data.successCount} sent, ${response.data.failureCount} failed.`);
      }
      
      // Clear form after 2 seconds
      setTimeout(() => {
        handleCloseInviteDialog();
      }, 2000);
    } catch (err) {
      setError(isEndedMeeting(selectedMeetingForInvite) ? 'Failed to send meeting materials' : 'Failed to send invitations');
    }
  };

  const handleOpenUploadDialog = (meeting) => {
    setSelectedMeetingForUpload(meeting);
    setUploadFile(null);
    setUploadProgress(0);
    setOpenUploadDialog(true);
  };

  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
    setSelectedMeetingForUpload(null);
    setUploadFile(null);
    setUploadProgress(0);
  };

  const handleFileSelect = (event) => {
    setUploadFile(event.target.files[0]);
  };

  const handleUploadRecording = async () => {
    if (!selectedMeetingForUpload || !uploadFile) return;

    try {
      setUploading(true);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', selectedMeetingForUpload.title);
      formData.append('description', selectedMeetingForUpload.description || '');
      formData.append('hostedMeetingId', selectedMeetingForUpload.id);
      
      // Upload to meetings endpoint which will trigger transcription
      await api.post(
        '/meetings/upload',
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      alert('Recording uploaded successfully! Transcription will begin shortly.');
      handleCloseUploadDialog();
      fetchMeetings();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Failed to upload recording');
      setUploading(false);
    }
  };

  const handleTranscribeRecording = async (meeting) => {
    if (!meeting.id) {
      setError('Invalid meeting ID');
      return;
    }

    if (!window.confirm('Start transcription for this recording? This may take several minutes to complete.')) {
      return;
    }

    setTranscribing(prev => ({ ...prev, [meeting.id]: true }));
    setError('');

    try {
      await api.post(
        `/hosted-meetings/${meeting.id}/process`,
        { meeting_id: meeting.id }
      );

      // Show success via existing error state mechanism (will auto-clear)
      setError('');
      fetchMeetings();
    } catch (err) {
      console.error('Transcription error:', err);
      setError(err.response?.data?.error || 'Failed to start transcription');
    } finally {
      setTranscribing(prev => ({ ...prev, [meeting.id]: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'waiting': return 'warning';
      case 'scheduled': return 'info';
      case 'ended': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography>Loading meetings...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Meeting Host Dashboard
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<VideoCallIcon />}
              onClick={handleStartInstantMeeting}
              sx={{ mr: 2 }}
            >
              Start Instant Meeting
            </Button>
            <Button
              variant="outlined"
              startIcon={<ScheduleIcon />}
              onClick={() => setOpenCreateDialog(true)}
            >
              Schedule Meeting
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {meetings.map((meeting) => (
            <Grid item xs={12} md={6} key={meeting.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" component="div">
                      {meeting.title}
                    </Typography>
                    <Chip 
                      label={meeting.status} 
                      color={getStatusColor(meeting.status)}
                      size="small"
                    />
                  </Box>
                  
                  {meeting.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {meeting.description}
                    </Typography>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Meeting Code: <strong>{meeting.meetingCode}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Host: {meeting.host.firstName} {meeting.host.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Participants: {meeting.participants?.length || 0}
                    </Typography>
                    {meeting.scheduledStartTime && (
                      <Typography variant="body2" color="text.secondary">
                        Scheduled: {new Date(meeting.scheduledStartTime).toLocaleString()}
                      </Typography>
                    )}
                    {meeting.recordings && meeting.recordings.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          Recording:
                        </Typography>
                        {meeting.recordings.map((recording) => {
                          // Check if there's an active conversion for this recording
                          const activeConversion = recording.conversionQueue && recording.conversionQueue.length > 0 
                            ? recording.conversionQueue[0] 
                            : null;
                          
                          if (activeConversion) {
                            // Show conversion status
                            return (
                              <Chip
                                key={recording.id}
                                label={`Converting ${activeConversion.sourceFormat.toUpperCase()} → ${activeConversion.targetFormat.toUpperCase()} (${activeConversion.status})`}
                                size="small"
                                color={activeConversion.status === 'processing' ? 'warning' : 'default'}
                              />
                            );
                          }
                          
                          // Show regular recording status
                          return (
                            <Chip
                              key={recording.id}
                              label={`${recording.format.toUpperCase()} - ${recording.status}`}
                              size="small"
                              color={
                                recording.status === 'completed' ? 'success' :
                                recording.status === 'processing' ? 'info' :
                                recording.status === 'failed' ? 'error' : 'default'
                              }
                            />
                          );
                        })}
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {meeting.status === 'ended' ? (
                      (() => {
                        const availableRecording = meeting.recordings?.find(r => r.status === 'completed' || r.status === 'processing');
                        const transcribedRecording = meeting.recordings?.find(r => r.transcriptId && r.transcript?.status === 'completed');
                        // Check if any recording has a transcript that is currently processing
                        const isTranscriptionProcessing = meeting.recordings?.some(r => 
                          r.transcript && r.transcript.status === 'processing'
                        );
                        
                        return availableRecording ? (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<DownloadIcon />}
                              onClick={() => handleViewRecording(meeting)}
                            >
                              View Recording
                            </Button>
                            {meeting.meetingType === 'instant' && !transcribedRecording && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<TranscribeIcon />}
                                onClick={() => handleTranscribeRecording(meeting)}
                                disabled={transcribing[meeting.id] || isTranscriptionProcessing}
                              >
                                {(transcribing[meeting.id] || isTranscriptionProcessing) ? 'Transcribing...' : 'Transcribe'}
                              </Button>
                            )}
                            {transcribedRecording && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<TranscribeIcon />}
                                onClick={() => navigate(`/meeting/${transcribedRecording.transcriptId}`)}
                              >
                                Review
                              </Button>
                            )}
                          </>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<LaunchIcon />}
                            onClick={() => navigate(`/meeting-room/${meeting.id}`)}
                          >
                            View
                          </Button>
                        );
                      })()
                    ) : (meeting.status === 'scheduled' || meeting.status === 'waiting' || meeting.status === 'active') && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<LaunchIcon />}
                        onClick={() => navigate(`/meeting-room/${meeting.id}`)}
                      >
                        {meeting.status === 'active' ? 'Rejoin' : 'Join'}
                      </Button>
                    )}
                    {meeting.status === 'active' && (
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        startIcon={<CallEndIcon />}
                        onClick={() => handleEndMeeting(meeting.id)}
                      >
                        End
                      </Button>
                    )}
                    {meeting.status === 'ended' && !meeting.recordings?.find(r => r.status === 'completed' || r.status === 'processing') && (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => handleOpenUploadDialog(meeting)}
                      >
                        Upload Recording
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleOpenInviteDialog(meeting)}
                      title="Send invitations"
                      color="primary"
                    >
                      <EmailIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => copyMeetingLink(meeting.meetingCode)}
                      title="Copy meeting link"
                    >
                      <CopyIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMeeting(meeting.id)}
                      title="Delete meeting"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardContent>
                {meeting.status === 'ended' && (
                  <MeetingAnalyticsCard meetingId={meeting.id} />
                )}
              </Card>
            </Grid>
          ))}

          {meetings.length === 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <VideoCallIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No meetings yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Start an instant meeting or schedule one for later
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Create Meeting Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule New Meeting</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Meeting Title"
              value={newMeeting.title}
              onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={newMeeting.description}
              onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Password (optional)"
              type="password"
              value={newMeeting.password}
              onChange={(e) => setNewMeeting({ ...newMeeting, password: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Start Date & Time"
              type="datetime-local"
              value={newMeeting.scheduledStartTime}
              onChange={(e) => setNewMeeting({ ...newMeeting, scheduledStartTime: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />
            <TextField
              fullWidth
              label="End Date & Time"
              type="datetime-local"
              value={newMeeting.scheduledEndTime}
              onChange={(e) => setNewMeeting({ ...newMeeting, scheduledEndTime: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newMeeting.allowGuests}
                  onChange={(e) => setNewMeeting({ ...newMeeting, allowGuests: e.target.checked })}
                />
              }
              label="Allow Guests"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newMeeting.recordingEnabled}
                  onChange={(e) => setNewMeeting({ ...newMeeting, recordingEnabled: e.target.checked })}
                />
              }
              label="Enable Recording"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newMeeting.transcriptionEnabled}
                  onChange={(e) => setNewMeeting({ ...newMeeting, transcriptionEnabled: e.target.checked })}
                />
              }
              label="Enable Transcription"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateMeeting} 
            variant="contained"
            disabled={!newMeeting.title || !newMeeting.scheduledStartTime}
          >
            Create Meeting
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={openInviteDialog} onClose={handleCloseInviteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedMeetingForInvite && isEndedMeeting(selectedMeetingForInvite) 
            ? 'Send Meeting Materials' 
            : 'Send Meeting Invitations'}
          {selectedMeetingForInvite && (
            <Typography variant="body2" color="text.secondary">
              {selectedMeetingForInvite.title}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {inviteSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {inviteSuccess}
              </Alert>
            )}
            
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedMeetingForInvite && isEndedMeeting(selectedMeetingForInvite)
                ? 'Add email addresses to send meeting materials (recording, transcription, and summary):'
                : 'Add email addresses to send meeting invitations:'}
            </Typography>

            {invitees.map((invitee, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={invitee.email}
                  onChange={(e) => handleInviteeChange(index, 'email', e.target.value)}
                  size="small"
                  required
                />
                <TextField
                  fullWidth
                  label="Name (optional)"
                  value={invitee.name}
                  onChange={(e) => handleInviteeChange(index, 'name', e.target.value)}
                  size="small"
                />
                {invitees.length > 1 && (
                  <IconButton
                    onClick={() => handleRemoveInvitee(index)}
                    color="error"
                    size="small"
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddInvitee}
              size="small"
              sx={{ mt: 1 }}
            >
              {selectedMeetingForInvite && isEndedMeeting(selectedMeetingForInvite)
                ? 'Add Another Recipient'
                : 'Add Another Invitee'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteDialog}>Cancel</Button>
          <Button 
            onClick={handleSendInvitations} 
            variant="contained"
            disabled={invitees.every(inv => !inv.email.trim())}
            startIcon={<EmailIcon />}
          >
            {selectedMeetingForInvite && isEndedMeeting(selectedMeetingForInvite)
              ? 'Send Materials'
              : 'Send Invitations'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Recording Dialog */}
      <Dialog open={openUploadDialog} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload Recording
          {selectedMeetingForUpload && (
            <Typography variant="body2" color="text.secondary">
              {selectedMeetingForUpload.title}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Upload the meeting recording to automatically start transcription.
            </Typography>

            <input
              accept="audio/*,video/*"
              style={{ display: 'none' }}
              id="recording-file-upload"
              type="file"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <label htmlFor="recording-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                disabled={uploading}
                fullWidth
                sx={{ py: 2, mb: 2 }}
              >
                {uploadFile ? 'Change File' : 'Select Recording File'}
              </Button>
            </label>

            {uploadFile && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Selected File:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {uploadFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Size: {(uploadFile.size / (1024 * 1024)).toFixed(2)} MB
                </Typography>
              </Box>
            )}

            {uploading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Uploading... {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              Supported formats: MP3, WAV, M4A, MP4, AVI, MOV (Max 500MB)
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUploadRecording} 
            variant="contained"
            disabled={!uploadFile || uploading}
            startIcon={<CloudUploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload & Transcribe'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default HostDashboard;
